import errorOutline from "@/assets/img/error_outline.png";
import type { Workflow } from "@/types/resources";
import { MoreVert } from "@mui/icons-material";
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import type { FC } from "react";
import { useParams } from "react-router-dom";
import type { MenuType } from "../";
import type { CustomTheme } from "../../../../theme";
import { useNavigation } from "../hooks/useNavigation";
import { useScrollPosition } from "../hooks/useScrollPosition";

type Props = {
  workflow: Workflow;
  onClickMenu: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    menuItem: MenuType
  ) => void;
};

export const WorkflowListItem: FC<Props> = ({ workflow, onClickMenu }) => {
  const theme = useTheme<CustomTheme>();
  const { workflow_id } = useParams<{
    workflow_id: string;
  }>();
  const { saveScrollPosition } = useScrollPosition();

  const menuItem = convertUnassignedWorkflowToMenuType(workflow);

  const { handleClickMenuItem } = useNavigation();

  const isActive = menuItem.format_id === workflow_id;

  return (
    <ListItem
      secondaryAction={
        <IconButton
          edge="end"
          onClick={(event) => {
            saveScrollPosition();
            onClickMenu(event, menuItem);
          }}
        >
          <MoreVert
            sx={{
              fill: isActive ? "white" : undefined,
            }}
          />
        </IconButton>
      }
      disablePadding
      data-workflow-id={workflow.id}
    >
      <StyledListItemButton
        theme={theme}
        active={isActive.toString()}
        onClick={() => {
          saveScrollPosition();
          handleClickMenuItem(`/views/${menuItem.format_id}`);
        }}
      >
        <ListItemText
          primaryTypographyProps={{
            flex: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            fontWeight: "bold",
          }}
          primary={menuItem.label}
        />
        {!menuItem.set_train_data &&
          menuItem.use_llm &&
          !menuItem.is_full_text_ocr &&
          menuItem.workflow_type === "ocr" && (
            <Tooltip
              title={
                <span>
                  正解データが設定されていません。
                  <br />
                  正解データをAIに学習させることで精度が向上します。
                </span>
              }
              placement="top"
              arrow
            >
              <img
                src={errorOutline}
                style={{
                  width: "14px",
                  marginLeft: "8px",
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
        {menuItem.workflow_type === "sorting" &&
          !menuItem.sorting_executable && (
            <Tooltip
              title={
                <span>
                  Scanのルールが設定されていません。
                  <br />
                  Scanのルールを設定することでドキュメント分析が実行可能となります。
                </span>
              }
              placement="top"
              arrow
            >
              <img
                src={errorOutline}
                style={{
                  width: "14px",
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          )}
      </StyledListItemButton>
    </ListItem>
  );
};

const StyledListItemButton = styled(ListItemButton)(
  ({ theme, active }: { theme: CustomTheme; active: string }) => {
    const isActive = active === "true";
    return {
      padding: "8px 20px",
      borderRadius: "10px",
      color: isActive ? "#FFFFFF" : "#333333",
      background: isActive
        ? theme.palette.primary.main
        : theme.palette.secondary.main,
      "&:hover": {
        backgroundColor: isActive
          ? theme.palette.primary.main
          : theme.customPalette.hover,
      },
      border: isActive ? undefined : `1px solid ${theme.customPalette?.border}`,
    };
  }
);

// フォルダに属さないワークフロー用の変換関数
const convertUnassignedWorkflowToMenuType = (workflow: Workflow): MenuType => {
  const latestFormatRule = workflow.format_rule;
  return {
    key: parseInt(workflow.id.replace(/[^0-9]/g, "")),
    label: workflow.workflow_name,
    open: false,
    use_llm: latestFormatRule?.use_llm || false,
    is_full_text_ocr: latestFormatRule?.is_full_text_ocr || false,
    set_train_data: !!(
      latestFormatRule?.input_example?.id && latestFormatRule?.id
    ),
    format_id: workflow.id,
    setting_menu_active: !latestFormatRule || latestFormatRule.use_llm,
    format_rule_item: latestFormatRule,
    workflow_type: workflow.workflow_type,
    sorting_executable: workflow.sorting_executable,
  };
};
